import { T } from '@tolgee/react';
import Link from 'next/link';
import { ReactNode } from 'react';
import styled from 'styled-components';

import rocketSticker from '../../../assets/svg/stickers/rocket.svg';
import theme from '../../../assets/theme';

export const ALL_CASE_STUDIES_HREF = '/case-studies';
/** Admin-facing CTA — demos are booked through the quotes page, same as the phonics hub. */
export const BOOK_A_DEMO_HREF = '/quotes';

export type ImpactEfficacyItem = {
  /** The audience the case study speaks to — this is what people scan for. */
  title: ReactNode;
  /** One-line result, always naming the district so the claim is checkable. */
  desc: ReactNode;
  link: string;
};

export type ImpactEfficacyGroup = {
  key: string;
  heading: ReactNode;
  items: ImpactEfficacyItem[];
};

/**
 * The Impact & Efficacy mega-menu is organised by *who the reader teaches*
 * rather than by district name, so a visitor can find the study that matches
 * their own classroom at a glance.
 */
export const EVERY_LEARNER_GROUP: ImpactEfficacyGroup = {
  key: 'every-learner',
  heading: (
    <T
      keyName="ImpactMenu.Group.EveryLearner"
      defaultValue="Better Writing Outcomes for Every Learner"
    />
  ),
  items: [
    {
      title: (
        <T
          keyName="ImpactMenu.Item.Sped.Title"
          defaultValue="Dyslexia & special education"
        />
      ),
      desc: (
        <T
          keyName="ImpactMenu.Item.Sped.Desc"
          defaultValue="Small-group ELAR instruction lifted 4th-graders in STAAR's top 40% from 6% to 49% — Flour Bluff ISD, TX."
        />
      ),
      link: '/case-studies/curipod-case-study-flour-bluff',
    },
    {
      title: (
        <T
          keyName="ImpactMenu.Item.Bilingual.Title"
          defaultValue="Emergent bilingual students"
        />
      ),
      desc: (
        <T
          keyName="ImpactMenu.Item.Bilingual.Desc"
          defaultValue="Sharply reduced STAAR ECR zero-scores and raised state writing results in just 45 minutes a day — Ysleta ISD, TX, two miles from the border."
        />
      ),
      link: '/case-studies/curipod-case-study-ysleta-bilingual',
    },
    {
      title: (
        <T
          keyName="ImpactMenu.Item.EnglishLearners.Title"
          defaultValue="English learners"
        />
      ),
      desc: (
        <T
          keyName="ImpactMenu.Item.EnglishLearners.Desc"
          defaultValue="5th-grade ELA CAASPP score rose 35%, beating the district benchmark — 87% FRPL, 94% Hispanic, Southern California."
        />
      ),
      link: '/case-studies/curipod-case-study-southern-ca',
    },
    {
      title: (
        <T
          keyName="ImpactMenu.Item.Econ.Title"
          defaultValue="Economically disadvantaged students"
        />
      ),
      desc: (
        <T
          keyName="ImpactMenu.Item.Econ.Desc"
          defaultValue="6th-grade STAAR Meets & Masters climbed 46%→84% over two years — 88% FRPL, South Texas."
        />
      ),
      link: '/case-studies/curipod-case-study-south-east-texas',
    },
    {
      title: (
        <T keyName="ImpactMenu.Item.Rural.Title" defaultValue="Small & rural districts" />
      ),
      desc: (
        <T
          keyName="ImpactMenu.Item.Rural.Desc"
          defaultValue="8th-grade STAAR ELAR and Social Studies both reached 100% Meets/Approaches+, lifting the district from a C to an A in one year — Sivells Bend ISD, TX."
        />
      ),
      link: '/case-studies/curipod-case-study-sivells-bend',
    },
    {
      title: (
        <T keyName="ImpactMenu.Item.Virtual.Title" defaultValue="Virtual learners" />
      ),
      desc: (
        <T
          keyName="ImpactMenu.Item.Virtual.Desc"
          defaultValue="7th-grade ELA grew +19% year-over-year alongside 95% attendance — the only school in its district to hit it — K-8 virtual, Palmdale SD, CA."
        />
      ),
      link: '/case-studies/curipod-case-study-innovations-academy',
    },
  ],
};

export const ELA_GROWTH_GROUP: ImpactEfficacyGroup = {
  key: 'ela-growth',
  heading: (
    <T
      keyName="ImpactMenu.Group.ElementarySecondary"
      defaultValue="For Elementary & Secondary"
    />
  ),
  items: [
    {
      title: (
        <T
          keyName="ImpactMenu.Item.EarlyElem.Title"
          defaultValue="Early-elementary readers & writers"
        />
      ),
      desc: (
        <T
          keyName="ImpactMenu.Item.EarlyElem.Desc"
          defaultValue="3rd-grade STAAR RLA passing jumped from 69% to 89% — Schertz-Cibolo-Universal City ISD, TX."
        />
      ),
      link: '/case-studies/curipod-case-study-green-valley',
    },
    {
      title: (
        <T
          keyName="ImpactMenu.Item.UpperElem.Title"
          defaultValue="Upper-elementary writers"
        />
      ),
      desc: (
        <T
          keyName="ImpactMenu.Item.UpperElem.Desc"
          defaultValue="After ~1 month of practice, students Near/Above Standard rose 46% — Southern California."
        />
      ),
      link: '/case-studies/california-writing-achievement',
    },
    {
      title: (
        <T
          keyName="ImpactMenu.Item.Reluctant.Title"
          defaultValue="Reluctant middle-school writers"
        />
      ),
      desc: (
        <T
          keyName="ImpactMenu.Item.Reluctant.Desc"
          defaultValue="6th-grade RLA Meets & Masters rose 22% using Curipod just once or twice a week — 88% economically disadvantaged, South Texas."
        />
      ),
      link: '/case-studies/curipod-case-study-south-texas',
    },
  ],
};

export const ENGAGEMENT_GROUP: ImpactEfficacyGroup = {
  key: 'engagement',
  heading: (
    <T keyName="ImpactMenu.Group.Engagement" defaultValue="Engagement & Confidence" />
  ),
  items: [
    {
      title: (
        <T
          keyName="ImpactMenu.Item.Quiet.Title"
          defaultValue="Quiet & disengaged students"
        />
      ),
      desc: (
        <T
          keyName="ImpactMenu.Item.Quiet.Desc"
          defaultValue="Within weeks, students who rarely participated were competing for top-voted answers — middle-school ELA, Meads Mill Middle School, Northville, MI."
        />
      ),
      link: '/case-studies/curipod-case-study-michigan',
    },
  ],
};

/** Reading order of the menu — also drives the mobile drawer. */
export const IMPACT_EFFICACY_GROUPS = [
  EVERY_LEARNER_GROUP,
  ELA_GROWTH_GROUP,
  ENGAGEMENT_GROUP,
];

const MenuGroup = ({ group }: { group: ImpactEfficacyGroup }) => (
  <>
    <GroupHeading>{group.heading}</GroupHeading>
    {group.items.map(item => (
      <Link href={item.link} passHref key={item.link}>
        <MenuItemLink>
          <ItemTitle>{item.title}</ItemTitle>
          <ItemDesc>{item.desc}</ItemDesc>
        </MenuItemLink>
      </Link>
    ))}
  </>
);

export const ImpactEfficacyMenu = () => (
  <Panel>
    <Columns>
      <Column>
        <MenuGroup group={EVERY_LEARNER_GROUP} />
      </Column>
      <Column>
        <MenuGroup group={ELA_GROWTH_GROUP} />
        <SecondGroup>
          <MenuGroup group={ENGAGEMENT_GROUP} />
        </SecondGroup>
      </Column>
    </Columns>
    <Footer>
      <FooterLeft>
        <img src={rocketSticker.src} alt="" />
        <span>
          <T
            keyName="ImpactMenu.Footer.Tagline"
            defaultValue="Based on the Science of Writing"
          />
        </span>
      </FooterLeft>
      <FooterLinks>
        <Link href={ALL_CASE_STUDIES_HREF} passHref>
          <a>
            <T
              keyName="ImpactMenu.Footer.AllCaseStudies"
              defaultValue="See all case studies & research"
            />{' '}
            &rarr;
          </a>
        </Link>
        <Link href={BOOK_A_DEMO_HREF} passHref>
          <a>
            <T keyName="ImpactMenu.Footer.BookDemo" defaultValue="Book a demo" /> &rarr;
          </a>
        </Link>
      </FooterLinks>
    </Footer>
  </Panel>
);

const HAIRLINE = 'rgba(40, 47, 51, 0.06)';

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  width: 900px;
  max-width: calc(100vw - 40px);
  background: ${theme.colors.white};
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 0.5px 1px rgba(40, 47, 51, 0.06), 0 18px 50px -10px rgba(40, 47, 51, 0.22);

  /* The dropdown lives inside an antd Menu, which otherwise imposes its own
     list typography on everything nested below it. */
  a {
    text-decoration: none;
  }
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 20px;
  border-right: 1px solid ${HAIRLINE};

  &:last-child {
    border-right: 0;
  }

  @media (max-width: 1100px) {
    border-right: 0;
    border-bottom: 1px solid ${HAIRLINE};
  }
`;

const SecondGroup = styled.div`
  margin-top: 22px;
`;

const GroupHeading = styled.div`
  font-size: 15px;
  font-weight: 900;
  letter-spacing: 0.02em;
  color: ${theme.colors.black};
  margin-bottom: 12px;
  padding: 0 12px 8px;
  border-bottom: 2px solid ${theme.colors.orange};
`;

const MenuItemLink = styled.a`
  display: block;
  position: relative;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.15s ease-in;

  &:hover {
    background-color: ${theme.colors.paleBeige};
  }

  /* Hairline between adjacent rows, so a dense list still reads as a list. */
  & + &::before {
    content: '';
    position: absolute;
    left: 12px;
    right: 12px;
    top: -1px;
    border-top: 1px solid ${theme.colors.beigeDark};
  }
`;

const ItemTitle = styled.div`
  font-size: 16px;
  font-weight: 900;
  line-height: 1.25;
  color: ${theme.colors.black};
  margin-bottom: 4px;
`;

const ItemDesc = styled.div`
  font-size: 13px;
  font-weight: 700;
  line-height: 1.45;
  color: ${theme.colors.greyDark};
`;

const Footer = styled.div`
  background: ${theme.colors.paleBeige};
  border-top: 1px solid ${HAIRLINE};
  padding: 14px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  font-size: 13px;
  font-weight: 800;

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${theme.colors.greyDark};

  img {
    height: 22px;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;

  a {
    color: ${theme.colors.black};
    white-space: nowrap;
    transition: color 0.2s ease;

    &:hover {
      color: ${theme.colors.orangeDark};
    }
  }
`;
